// ═══════════════════════════════════════════════════════════════════════════
// perfilVentaHistorico — Clasifica una publicación según QUÉ palancas usó
//                       históricamente para vender (publi y/o descuento).
// ───────────────────────────────────────────────────────────────────────────
// Sirve al motor de decisión de Central de Promociones para responder:
//   "¿conviene mantener publi y descuento, o apagar la palanca redundante?"
//
// Regla del negocio (definida con el usuario, 2026-05-03):
//   • Vendió con publi + descuento → apagás la palanca más cara (descuento%
//     vs ACOS%).
//   • Vendió solo con publi (sin descuento) → no sumarse a la promo (la
//     publi alcanza, descuento es regalo).
//   • Vendió solo con descuento (sin publi) → sumarse apagando publi (la
//     publi sería redundante).
//   • Vende solo (sin nada) → candidato a promo propia si margen sobrado.
//
// FUENTES DE DATOS:
//   • Publi por orden — exacta. mlVentas/{id}._rentabilidad.porLinea[oiId].publicidad
//     >0 = la venta tuvo publi atribuida.
//   • Descuento por orden — inferido. unitPrice de la orden vs precio actual de
//     la publicación. Si unitPrice < price (con tolerancia) y la publi tiene
//     `original_price > price`, se considera que la venta fue con descuento.
//     No es 100% certero (el precio de la publi puede haber cambiado), pero
//     a nivel agregado (varias órdenes) la señal es buena.
//
// Las clasificaciones requieren un mínimo de órdenes (default 5) para ser
// confiables. Por debajo se devuelve perfil 'pocos-datos'.
// ═══════════════════════════════════════════════════════════════════════════

// ─── Diccionario de perfiles ────────────────────────────────────────────────

export const PERFIL_VENTA = Object.freeze({
  conPubliConDesc: {
    id: 'con-publi-con-desc',
    label: 'Vende con publi + descuento',
    descripcion: 'Histórico mixto: parte de las ventas usó publi, parte usó descuento. Ante una promo, conviene apagar la palanca más cara.',
    emoji: '📣🎁',
  },
  soloPubli: {
    id: 'solo-publi',
    label: 'Vende solo con publi',
    descripcion: 'Histórico dominado por publi, sin descuento. Si entra una promo, el descuento es redundante: no sumarse o pasar publi a promo.',
    emoji: '📣',
  },
  soloDesc: {
    id: 'solo-desc',
    label: 'Vende solo con descuento',
    descripcion: 'Histórico dominado por descuento, sin publi. Si está prendida la publi, conviene apagarla durante la promo.',
    emoji: '🎁',
  },
  vendeSolo: {
    id: 'vende-solo',
    label: 'Vende solo',
    descripcion: 'Casi todas las ventas sin publi ni descuento. Producto autónomo — candidato a promo propia si margen sobrado.',
    emoji: '✨',
  },
  pocosDatos: {
    id: 'pocos-datos',
    label: 'Pocos datos',
    descripcion: 'Histórico insuficiente para inferir un perfil confiable.',
    emoji: '❓',
  },
})

// Tolerancia para considerar que un unitPrice histórico estuvo "por debajo"
// del precio actual — cubre redondeos y movimientos chicos de precio.
const TOLERANCIA_DESCUENTO = 0.02 // 2%

// ─── Helpers ────────────────────────────────────────────────────────────────

function num(v) {
  const n = Number(v)
  return Number.isFinite(n) ? n : 0
}

/**
 * Determina si una orden tuvo publi atribuida.
 * `linea.publicidad > 0` = sí; `=== 0` = no; `null/undefined` = sin info.
 */
function tuvoPubli(linea) {
  if (linea == null) return null
  const v = linea.publicidad
  if (v == null) return null
  return num(v) > 0
}

/**
 * Determina si una orden tuvo descuento aplicado.
 * Compara unitPrice histórico contra precio actual de la publicación. Si la
 * publi tiene original_price > price, asume que el "precio actual" es el
 * promocional, así que comparamos contra original_price.
 *
 * @param {object} linea  — fila de _rentabilidad.porLinea
 * @param {object} pub    — doc de mlPublicaciones (para original_price/price)
 * @returns {boolean|null} — true/false/null si no se puede inferir
 */
function tuvoDescuento(linea, pub) {
  if (!linea || !pub) return null
  const qty = num(linea.qty)
  const subtotal = num(linea.subtotal)
  if (qty <= 0 || subtotal <= 0) return null
  const unitPrice = subtotal / qty
  const price = num(pub.price)
  const orig = num(pub.original_price ?? pub.originalPrice)
  if (price <= 0) return null
  // Si la publi NO tiene original_price > price, comparamos contra price actual.
  // Si la publi TIENE original_price > price (descuento vigente), comparamos
  // contra original_price — ese es el precio "sin descuento".
  const referencia = orig > price ? orig : price
  return unitPrice < referencia * (1 - TOLERANCIA_DESCUENTO)
}

// ─── Clasificador puro ──────────────────────────────────────────────────────

/**
 * Clasifica el perfil de venta de una publicación a partir de sus líneas
 * de orden (ya con _rentabilidad calculada) y el doc de la publicación.
 *
 * @param {object} args
 * @param {Array<object>} args.lineas       — array de líneas (con qty, subtotal, publicidad, estado)
 * @param {object} args.pub                 — doc mlPublicaciones (price, original_price)
 * @param {object} [args.opciones]
 * @param {number} [args.opciones.minOrdenes=5]    — órdenes mínimas para perfil confiable
 * @param {number} [args.opciones.umbralAlto=0.5]  — % de ventas para "vende con X"
 * @param {number} [args.opciones.umbralBajo=0.2]  — % de ventas para "no necesita X"
 *
 * @returns {object} — { perfil: PERFIL_VENTA[*], totalOrdenes, conPubli, conDesc, pctConPubli, pctConDesc }
 */
export function clasificarPerfilVenta({ lineas, pub, opciones = {} }) {
  const minOrdenes = opciones.minOrdenes ?? 5
  const umbralAlto = opciones.umbralAlto ?? 0.5
  const umbralBajo = opciones.umbralBajo ?? 0.2

  // Solo consideramos líneas calculadas (con costo y publi atribuida).
  const calculadas = (lineas || []).filter(l => l && l.estado === 'calculada')
  const total = calculadas.length

  if (total < minOrdenes) {
    return {
      perfil: PERFIL_VENTA.pocosDatos,
      totalOrdenes: total,
      conPubli: 0,
      conDesc: 0,
      pctConPubli: null,
      pctConDesc: null,
      confiable: false,
    }
  }

  let conPubli = 0
  let sinPubliInfo = 0
  let conDesc = 0
  let sinDescInfo = 0

  for (const l of calculadas) {
    const flagPubli = tuvoPubli(l)
    if (flagPubli === true) conPubli++
    else if (flagPubli === null) sinPubliInfo++

    const flagDesc = tuvoDescuento(l, pub)
    if (flagDesc === true) conDesc++
    else if (flagDesc === null) sinDescInfo++
  }

  // Para los porcentajes usamos las líneas que SÍ tienen info, no el total
  // bruto (evita subestimar cuando faltan datos).
  const baseConPubli = total - sinPubliInfo
  const baseConDesc = total - sinDescInfo
  const pctConPubli = baseConPubli > 0 ? conPubli / baseConPubli : null
  const pctConDesc = baseConDesc > 0 ? conDesc / baseConDesc : null

  // ─── Clasificación ─────────────────────────────────────────────────────────
  // 4 cuadrantes según si "vende con publi" y "vende con descuento":
  //
  //                 desc bajo (< umbralBajo)   desc alto (>= umbralAlto)
  //   publi bajo    vendeSolo                  soloDesc
  //   publi alto    soloPubli                  conPubliConDesc
  //
  // Casos intermedios (entre umbralBajo y umbralAlto) caen al cuadrante "alto"
  // del eje correspondiente para no perder información — preferimos sobreestimar
  // que el item usa una palanca, antes que ignorarla.

  const usaPubli = pctConPubli != null && pctConPubli >= umbralBajo
  const usaDesc = pctConDesc != null && pctConDesc >= umbralBajo

  let perfil
  if (usaPubli && usaDesc) perfil = PERFIL_VENTA.conPubliConDesc
  else if (usaPubli && !usaDesc) perfil = PERFIL_VENTA.soloPubli
  else if (!usaPubli && usaDesc) perfil = PERFIL_VENTA.soloDesc
  else perfil = PERFIL_VENTA.vendeSolo

  return {
    perfil,
    totalOrdenes: total,
    conPubli,
    conDesc,
    pctConPubli,
    pctConDesc,
    confiable: true,
  }
}

/**
 * Indexa un array de mlVentas en un map { itemId → [lineas] } para que el
 * clasificador pueda consultar las líneas de cada publicación sin recorrer
 * todas las órdenes cada vez.
 *
 * @param {Array<object>} ventas — docs de mlVentas con _rentabilidad.porLinea
 * @returns {Map<string, Array<object>>}
 */
export function indexarLineasPorItem(ventas) {
  const idx = new Map()
  for (const v of ventas || []) {
    const porLinea = v?._rentabilidad?.porLinea
    if (!porLinea) continue
    for (const oiId of Object.keys(porLinea)) {
      const linea = porLinea[oiId]
      const itemId = linea?._itemId
      if (!itemId) continue
      if (!idx.has(itemId)) idx.set(itemId, [])
      idx.get(itemId).push(linea)
    }
  }
  return idx
}

/**
 * Indexa por familyId (agrupa todas las variaciones bajo el mismo padre).
 * Si una publicación no tiene familyId, se indexa por su itemId.
 *
 * @param {Array<object>} ventas       — docs de mlVentas
 * @param {Map<string, object>} pubsById — map itemId → mlPublicaciones (para resolver familyId)
 * @returns {Map<string, Array<object>>} — map familyId → [lineas]
 */
export function indexarLineasPorFamilia(ventas, pubsById) {
  const idx = new Map()
  for (const v of ventas || []) {
    const porLinea = v?._rentabilidad?.porLinea
    if (!porLinea) continue
    for (const oiId of Object.keys(porLinea)) {
      const linea = porLinea[oiId]
      const itemId = linea?._itemId
      if (!itemId) continue
      const pub = pubsById?.get(itemId)
      const key = pub?.familyId || itemId
      if (!idx.has(key)) idx.set(key, [])
      idx.get(key).push(linea)
    }
  }
  return idx
}
